/**
 * Extraction helpers for debug field definitions
 *
 * These utilities reduce duplication in field extractors by providing
 * common patterns for extracting values from bondAllocations or cashFlow.
 */

import type { BondPaymentCashFlowRecord } from '~/types';
import type { DebugExtractionContext } from '../types';

/** Numeric fields available on BondAllocation */
type BondAllocationNumericField =
  | 'principal'
  | 'interestRate'
  | 'newInterest'
  | 'futureInterestObligation'
  | 'totalInterest'
  | 'totalPayment';

/**
 * Type-safe accessor for numeric fields on BondPaymentCashFlowRecord.
 * Uses a switch statement to ensure type safety and catch field name errors at compile time.
 */
function getBondPaymentField(
  bp: BondPaymentCashFlowRecord,
  field: BondAllocationNumericField
): number {
  switch (field) {
    case 'principal':
      return bp.principal ?? 0;
    case 'interestRate':
      return bp.interestRate ?? 0;
    case 'newInterest':
      return bp.newInterest ?? 0;
    case 'futureInterestObligation':
      return bp.futureInterestObligation ?? 0;
    case 'totalInterest':
      return bp.totalInterest ?? 0;
    case 'totalPayment':
      return bp.totalPayment ?? 0;
  }
}

/**
 * Sum a numeric field across all bond allocations.
 * Prefers bondAllocations, falls back to cashFlow.bondPayments.
 *
 * @param ctx - Debug extraction context
 * @param field - Numeric field name (e.g., 'principal', 'newInterest')
 * @returns Sum of the field values, or undefined if no data available
 */
export function sumBondField(
  ctx: DebugExtractionContext,
  field: BondAllocationNumericField
): number | undefined {
  // Prefer bondAllocations (calculated values at capture time)
  // Empty array = no active bonds = sum of 0 (not undefined)
  if (ctx.bondAllocations) {
    return ctx.bondAllocations.reduce((sum, alloc) => {
      const value = alloc[field];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);
  }

  // Fallback to cashFlow.bondPayments (only if bondAllocations not provided)
  const cf = ctx.cashFlow;
  if (!cf?.bondPayments) return undefined;

  return cf.bondPayments.reduce((sum, bp) => sum + getBondPaymentField(bp, field), 0);
}

/**
 * Get the maximum value of a numeric field across all bond allocations.
 * Prefers bondAllocations, falls back to cashFlow.bondPayments.
 *
 * @param ctx - Debug extraction context
 * @param field - Numeric field name on BondAllocation
 * @param activeOnly - If true, only consider active bonds (cashFlow fallback only)
 * @returns Maximum value, or undefined if no data available
 */
export function maxBondField(
  ctx: DebugExtractionContext,
  field: BondAllocationNumericField,
  activeOnly = true
): number | undefined {
  // Prefer bondAllocations (calculated values at capture time)
  // Note: bondAllocations only contains active bonds by design
  // Empty array = no active bonds = max of 0
  if (ctx.bondAllocations) {
    if (ctx.bondAllocations.length === 0) return 0;
    return Math.max(
      ...ctx.bondAllocations.map((alloc) => {
        const value = alloc[field];
        return typeof value === 'number' ? value : 0;
      })
    );
  }

  // Fallback to cashFlow.bondPayments (only if bondAllocations not provided)
  const cf = ctx.cashFlow;
  if (!cf?.bondPayments) return undefined;

  const bonds = activeOnly ? cf.bondPayments.filter((bp) => bp.isActive) : cf.bondPayments;
  if (bonds.length === 0) return 0;

  return Math.max(...bonds.map((bp) => getBondPaymentField(bp, field)));
}

/**
 * Count the number of bond allocations.
 * Prefers bondAllocations, falls back to counting active bonds in cashFlow.
 *
 * @param ctx - Debug extraction context
 * @returns Count of bonds, or undefined if no data available
 */
export function countBonds(ctx: DebugExtractionContext): number | undefined {
  // Prefer bondAllocations (only contains active bonds)
  if (ctx.bondAllocations) {
    return ctx.bondAllocations.length;
  }

  // Fallback to cashFlow.bondPayments
  const cf = ctx.cashFlow;
  if (!cf?.bondPayments) return undefined;

  return cf.bondPayments.filter((bp) => bp.isActive).length;
}
